import { useEffect, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Accordion, ButtonText, Checkbox, useToast } from '@morressier/alembic';
import {
  faBell,
  faBook,
  faClock,
  faCopyright,
  faEdit,
  faHourglassHalf,
  faPencil
} from '@fortawesome/pro-regular-svg-icons';
import { useForm } from 'react-hook-form';

import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { RequestSubmissionDeadlineExtensionModal } from 'src/components/Submission/decisionModals/RequestSubmissionDeadlineExtensionModal';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { IconBadge } from 'src/components/IconBadge';
import useEditorialForm from 'src/hooks/useEditorialForm';
import ProtectedAction from 'src/components/ProtectedAction';
import { Utils } from '@publishing-engine/editorial-form-field-build/src';
import {
  IEditorialFormField,
  IWorkPhase,
  isAuthorPhase
} from '@publishing-engine/domain/models';
import { useUpdateEditorialFormData } from 'src/api/commands';
import { ERRORS, MY_SUBMISSION_TABS } from 'src/constants';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import { useQuery } from '@tanstack/react-query';
import { fetchOrganizationAndJournalsInformation } from '../../../../../api/queries';
import { timeFormatDeadlineDate } from '../../../../../utils/date';
import { getIcmjeDisclosureLabel } from '../../../../../utils/icmje';
import { isFormConfigured } from '../../../../../utils/formConfig';
import HighlightItem from '../../editorial/PeerReview/HighlightItem';
import { toSentenceCasePreserveAcronyms } from 'src/utils/string';
import { FieldLabel } from 'src/components/Field';

export const AuthorRevisionAdditionalInfo = ({
  setActiveTab
}: {
  setActiveTab: (tab: any) => JSX.Element;
}) => {
  const toast = useToast();
  const {
    submissionId,
    submissionContextId,
    workflowResponse,
    organizationId,
    icmjeForm,
    icmjeFormList,
    submissionContext
  } = useSubmissionData();

  const { workflowData, articleWorkflow } = workflowResponse;
  const [showRevisionExtensionModal, setShowRevisionExtensionModal] =
    useState<boolean>(false);
  const reviewInformationLink =
    submissionContext?.config?.reviewInformationLink || '';

  // This panel is rendered for both author revision phases, `revision` and
  // `immediateRevision`, and each phase has its own checklist, so the checklist
  // phase has to follow the workflow rather than be hardcoded.
  const phase: IWorkPhase = isAuthorPhase(workflowData.currentState)
    ? workflowData.currentState
    : 'revision';

  const { data: organizationData } = useQuery({
    queryKey: ['organization'],
    queryFn: () =>
      organizationId
        ? fetchOrganizationAndJournalsInformation(organizationId)
        : null
  });

  const fetchRevisionEditorialFormDataKey = [
    'submission-checklist',
    { submissionId, phase, articleWorkflowId: articleWorkflow.id }
  ];

  const {
    editorialFormSchema: authorRevisionChecklistFormSchema,
    editorialFormData: authorRevisionChecklistData,
    defaultFormValues
  } = useEditorialForm(
    submissionId,
    submissionContextId,
    articleWorkflow.organizationId,
    phase,
    articleWorkflow.id
  );

  const updateChecklistMutation = useUpdateEditorialFormData(
    submissionId,
    submissionContextId,
    articleWorkflow.id,
    phase,
    fetchRevisionEditorialFormDataKey,
    organizationId
  );

  const { reset, register } = useForm<Record<string, boolean>>({
    defaultValues: defaultFormValues
  });

  useEffect(() => {
    if (authorRevisionChecklistData?.caseData) {
      reset({
        ...defaultFormValues,
        ...(authorRevisionChecklistData?.caseData as Record<string, boolean>)
      });
    }
  }, [authorRevisionChecklistData, reset]);

  const updateChecklist = async (
    name: string,
    value: boolean,
    label: string
  ) => {
    const response = await updateChecklistMutation.mutateAsync({
      [name]: value
    });

    if (response.isSuccess) {
      return toast({
        variant: 'success',
        title: `${toSentenceCasePreserveAcronyms(label)} ${
          value ? 'checked' : 'unchecked'
        }`
      });
    } else {
      return toast({
        variant: 'error',
        title: ERRORS.somethingWentWrong
      });
    }
  };

  const renderCheckbox = (field: IEditorialFormField) => {
    const { name, component, params } = field;
    const { caseDataField, labels, validations } = params;

    const registerField = register(name);

    if (component !== 'Checkbox') {
      return null;
    }

    return (
      <Flex key={name} gap={2} flexDirection="column">
        <ProtectedAction
          relation="author"
          entityType="submission"
          entityId={submissionId}
          tooltipLabel="You do not have permission to perform this action"
        >
          <Checkbox
            id={name}
            ariaLabel={labels.main}
            {...registerField}
            onChange={e => {
              registerField.onChange(e);
              updateChecklist(name, e.target.checked, labels.main);
            }}
            value={
              Utils.JsonUtil.extractByPath({
                json: authorRevisionChecklistData,
                path: caseDataField
              }) as string
            }
            isChecked={Boolean(
              Utils.JsonUtil.extractByPath({
                json: authorRevisionChecklistData,
                path: caseDataField
              })
            )}
            isRequired={validations?.required}
          >
            <FieldLabel
              isRequired={validations?.required}
              label={labels.main}
              subText={''}
              helpText={labels.description}
            />
          </Checkbox>
        </ProtectedAction>
      </Flex>
    );
  };

  const deadline = workflowData.currentDeadline;
  const isExtensionRequested =
    workflowData.deadlineExtensions?.status === 'requested';
  const daysToDeadline = calculateDaysToDeadline(
    new Date(workflowData.currentDeadline ?? '')
  );

  return (
    <AdditionalInformationWrapper>
      <Flex flexDirection="column">
        {/* ----- Highlights ----- */}
        <Accordion
          chakraAccordionProps={{
            width: '100%',
            defaultIndex: 0,
            allowToggle: true
          }}
          items={[
            {
              title: (
                <Flex alignItems={'flex-start'} gap={2}>
                  <IconBadge icon={faBell} variant="neutral" />
                  <Flex flexDirection={'column'} marginLeft={2}>
                    <Text textStyle="headingItem">Highlights</Text>
                    <Text textStyle="textContext">
                      Review key updates and other relevant information in this
                      phase.
                    </Text>
                  </Flex>
                </Flex>
              ),
              content: (
                <Flex flexDirection="column" width="100%" gap={4}>
                  <Flex gap={3} alignItems="center">
                    <IconBadge icon={faEdit} variant="attention" />
                    <Text textStyle="headingItem">Revisions Requested</Text>
                    <ButtonText
                      marginLeft="auto"
                      onClick={() => {
                        setActiveTab?.(MY_SUBMISSION_TABS.FEEDBACK);
                      }}
                    >
                      View Feedback
                    </ButtonText>
                  </Flex>
                  {phase === 'revision' && (
                    <>
                      <Flex
                        borderTop="1px solid"
                        borderColor="neutralPrimaryBorder"
                      />
                      <Flex width="100%" gap={3} alignItems="start">
                        <>
                          <IconBadge icon={faBook} variant="attention" />
                          <Flex flexDirection="column" flex={1}>
                            <Text textStyle="headingItem">
                              View {organizationData?.organization.name}{' '}
                              Revision Guidelines
                            </Text>
                          </Flex>
                          <ButtonText
                            marginLeft="auto"
                            onClick={() =>
                              window.open(reviewInformationLink, '_blank')
                            }
                          >
                            View Guidelines
                          </ButtonText>
                        </>
                      </Flex>
                    </>
                  )}
                  <>
                    <Flex
                      borderTop="1px solid"
                      borderColor="neutralPrimaryBorder"
                    />
                    <Flex width="100%" gap={3} alignItems="start">
                      {isExtensionRequested ? (
                        <>
                          <IconBadge
                            icon={faHourglassHalf}
                            variant="attention"
                          />
                          <Flex flexDirection="column" flex={1}>
                            <Text textStyle="headingItem">
                              Author Revision Extension Requested
                            </Text>
                          </Flex>
                        </>
                      ) : (
                        <>
                          <IconBadge icon={faClock} variant="attention" />
                          <Flex flexDirection="column" flex={1}>
                            <Text textStyle="headingItem">
                              {deadline
                                ? `Deadline on ${timeFormatDeadlineDate(deadline)}`
                                : null}
                            </Text>
                            <Text textStyle="textContext">
                              {workflowData.deadlineExtensions?.status ===
                              'denied'
                                ? 'The editors have rejected your last extension request.'
                                : `You have ${daysToDeadline} days to submit your work.`}
                            </Text>
                          </Flex>
                        </>
                      )}
                      <ProtectedAction
                        relation="author"
                        entityType="submission"
                        entityId={submissionId}
                        tooltipLabel="You do not have permission to perform this action"
                      >
                        <ButtonText
                          marginLeft="auto"
                          disabled={isExtensionRequested}
                          onClick={() => setShowRevisionExtensionModal(true)}
                        >
                          Request Extension
                        </ButtonText>
                      </ProtectedAction>
                    </Flex>
                  </>
                  {phase === 'revision' &&
                  isFormConfigured(
                    submissionContext?.config?.authorICMJEModel
                  ) ? (
                    <>
                      <Flex
                        borderTop="1px solid"
                        borderColor="neutralPrimaryBorder"
                      />
                      <HighlightItem
                        variant="attention"
                        icon={faCopyright}
                        heading={getIcmjeDisclosureLabel(
                          icmjeFormList ?? [],
                          icmjeForm ?? []
                        )}
                        context="Forms and Disclosures are optional during revision, but
                          will be mandatory if the submission is accepted."
                        buttonOnClick={() => {
                          setActiveTab?.(MY_SUBMISSION_TABS.RIGHTS);
                        }}
                        buttonText={
                          icmjeForm?.[0]?.file.url ? 'Review Info' : 'View Form'
                        }
                      />
                    </>
                  ) : null}
                </Flex>
              )
            }
          ]}
        />

        {/* ----- Task Checklist ----- */}
        {(authorRevisionChecklistFormSchema?.formFields || []).length > 0 && (
          <>
            <Flex borderTop="1px solid" borderColor="neutralPrimaryBorder" />
            <Accordion
              chakraAccordionProps={{
                width: '100%',
                defaultIndex: 0,
                allowToggle: true
              }}
              items={[
                {
                  title: (
                    <Flex alignItems={'flex-start'} gap={2}>
                      <IconBadge icon={faPencil} variant="neutral" />
                      <Flex flexDirection={'column'} marginLeft={2}>
                        <Text textStyle="headingItem">Task Checklist</Text>
                        <Text textStyle="textContext">
                          Ensure all required steps are completed before
                          advancing in the submission process.
                        </Text>
                      </Flex>
                    </Flex>
                  ),
                  content: (
                    <Flex flexDirection="column" gap={2}>
                      <form>
                        <Flex gap={4} flexDirection="column">
                          {authorRevisionChecklistFormSchema?.formFields?.map(
                            (field: IEditorialFormField) =>
                              renderCheckbox(field)
                          )}
                        </Flex>
                      </form>
                    </Flex>
                  )
                }
              ]}
            />{' '}
          </>
        )}
      </Flex>

      {showRevisionExtensionModal && (
        <RequestSubmissionDeadlineExtensionModal
          onClose={() => setShowRevisionExtensionModal(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          label={phase}
        />
      )}
    </AdditionalInformationWrapper>
  );
};
